import Badge from 'antd/es/badge'
import { useEffect, useState, type ReactNode } from 'react'
import { UiIcon, type UiIconName } from '../icons'

export interface ShellNavItem {
  key: string
  label: string
  icon: UiIconName
  children?: readonly ShellNavItem[]
}

export interface TenantShellProps {
  activeKey: string
  children: ReactNode
  licenseLabel: string
  logoSrc: string
  navItems: readonly ShellNavItem[]
  notificationCount?: number
  readOnly?: boolean
  userName: string
  onLicenseClick?: () => void
  onNavigate: (key: string) => void
  onNotifications?: () => void
  onUser?: () => void
  onLogout?: () => void
}

export function TenantShell({
  activeKey,
  children,
  licenseLabel,
  logoSrc,
  navItems,
  notificationCount = 0,
  readOnly = false,
  userName,
  onLicenseClick,
  onNavigate,
  onNotifications,
  onUser,
  onLogout,
}: TenantShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        navItems
          .filter((item) => item.children?.some((child) => child.key === activeKey))
          .map((item) => item.key),
      ),
  )

  useEffect(() => {
    const activeGroup = navItems.find((item) =>
      item.children?.some((child) => child.key === activeKey),
    )
    if (!activeGroup) return

    setExpandedGroups((current) => {
      if (current.has(activeGroup.key)) return current
      return new Set([...current, activeGroup.key])
    })
  }, [activeKey, navItems])

  const navigateTo = (key: string) => {
    setMobileOpen(false)
    onNavigate(key)
  }
  const toggleGroup = (key: string) => {
    setExpandedGroups((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  const activateGroup = (item: ShellNavItem) => {
    const isCompactDesktop = window.matchMedia('(min-width: 721px) and (max-width: 1050px)').matches
    if (isCompactDesktop) {
      const activeChild = item.children?.find((child) => child.key === activeKey)
      navigateTo(activeChild?.key ?? item.children?.[0]?.key ?? item.key)
      return
    }
    toggleGroup(item.key)
  }

  return (
    <div className="yh-shell">
      <button
        className={mobileOpen ? 'yh-mobile-overlay is-visible' : 'yh-mobile-overlay'}
        type="button"
        aria-label="关闭导航"
        onClick={() => setMobileOpen(false)}
      />
      <aside
        id="tenant-navigation"
        className={mobileOpen ? 'yh-sidebar is-mobile-open' : 'yh-sidebar'}
      >
        <button
          className="yh-brand"
          type="button"
          onClick={() => navigateTo('dashboard')}
          aria-label="返回工作台"
        >
          <img src={logoSrc} alt="鲨鱼ERP" />
          <span>鲨鱼ERP</span>
        </button>
        <nav className="yh-nav" aria-label="租户后台导航">
          {navItems.map((item) => {
            if (item.children?.length) {
              const isExpanded = expandedGroups.has(item.key)
              const isParentActive = item.children.some((child) => child.key === activeKey)
              const childRegionId = `yh-nav-group-${item.key}`

              return (
                <div
                  className={isExpanded ? 'yh-nav__group is-expanded' : 'yh-nav__group'}
                  key={item.key}
                >
                  <button
                    type="button"
                    className={`yh-nav__item yh-nav__group-label${
                      isParentActive ? ' is-parent-active' : ''
                    }`}
                    aria-expanded={isExpanded}
                    aria-controls={childRegionId}
                    onClick={() => activateGroup(item)}
                  >
                    <UiIcon name={item.icon} size={21} />
                    <span>{item.label}</span>
                    <UiIcon className="yh-nav__group-arrow" name="arrow" size={15} />
                  </button>
                  <div id={childRegionId} className="yh-nav__children" aria-hidden={!isExpanded}>
                    {item.children.map((child) => (
                      <button
                        key={child.key}
                        type="button"
                        className={
                          activeKey === child.key
                            ? 'yh-nav__item yh-nav__child is-active'
                            : 'yh-nav__item yh-nav__child'
                        }
                        onClick={() => navigateTo(child.key)}
                        tabIndex={isExpanded ? 0 : -1}
                      >
                        <span>{child.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )
            }

            return (
              <button
                key={item.key}
                type="button"
                className={activeKey === item.key ? 'yh-nav__item is-active' : 'yh-nav__item'}
                onClick={() => navigateTo(item.key)}
              >
                <UiIcon name={item.icon} size={21} />
                <span>{item.label}</span>
              </button>
            )
          })}
        </nav>
        <div className="yh-sidebar__foot">鲨鱼ERP · 1688 → Ozon</div>
      </aside>
      <section className="yh-workspace">
        <header className="yh-topbar">
          <div className="yh-topbar__left">
            <button
              className="yh-mobile-menu"
              type="button"
              aria-label="打开导航"
              aria-expanded={mobileOpen}
              aria-controls="tenant-navigation"
              onClick={() => setMobileOpen(true)}
            >
              <UiIcon name="grid" />
            </button>
          </div>
          <div className="yh-topbar__actions">
            <button
              className={readOnly ? 'yh-license is-readonly' : 'yh-license'}
              type="button"
              onClick={onLicenseClick}
            >
              {licenseLabel}
              {readOnly ? '（只读）' : null}
            </button>
            <Badge count={notificationCount} size="small" overflowCount={99}>
              <button
                className="yh-icon-button"
                type="button"
                onClick={onNotifications}
                aria-label={`站内通知，${notificationCount} 条未读`}
              >
                <UiIcon name="bell" />
              </button>
            </Badge>
            <button className="yh-user-button" type="button" onClick={onUser} title={userName}>
              <span className="yh-avatar">
                <UiIcon name="user" size={18} />
              </span>
              <strong>{userName}</strong>
              <span aria-hidden="true">⌄</span>
            </button>
            {onLogout ? (
              <button className="yh-logout-button" type="button" onClick={onLogout}>
                退出
              </button>
            ) : null}
          </div>
        </header>
        <main className="yh-content">{children}</main>
      </section>
    </div>
  )
}
